// src/components/UserPortalLayout.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, Outlet } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import {
  Menu, X, Bell, User, LogOut,
  Home, FileText, ChevronDown
} from 'lucide-react';
import PortalSwitcher from './PortalSwitcher';
import BusinessCentralService from '../services/businessCentralService';

/**
 * UserPortalLayout - Main layout for user/employee portal
 */
const UserPortalLayout = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [notifPanelOpen, setNotifPanelOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [selectedNotif, setSelectedNotif] = useState(null);
  const { user, activeEnvironment, logout, getAccessToken } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.isAdmin === true;
  const notifRef = useRef(null);

  // Check if there are auth setup issues that need admin attention
  const [authIncomplete, setAuthIncomplete] = useState(false);
  const [authEnvironment, setAuthEnvironment] = useState('');

  const bcService = React.useMemo(() => {
    return new BusinessCentralService(getAccessToken);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeEnvironment]);

  // Check for auth issues on component mount
  React.useEffect(() => {
    if (isAdmin) {
      const hasAuthIssue = localStorage.getItem('adminAuthIncomplete') === 'true';
      const envName = localStorage.getItem('adminAuthEnvironment') || '';
      if (hasAuthIssue) {
        setAuthIncomplete(true);
        setAuthEnvironment(envName);
      }
    }
  }, [isAdmin]);

  // Fetch notifications only once employeeNo is available.
  // Do NOT fetch on activeEnvironment alone — employeeNo is required by the backend
  // to filter notifications to this employee only. Without it the backend returns
  // all notifications for the entire company (data leak).
  useEffect(() => {
    const handleEmployeeNoReady = () => {
      const employeeNo = localStorage.getItem('employeeNo');
      if (!employeeNo) return;
      bcService.getNotifications().then(data => setNotifications(data || [])).catch(() => {});
    };
    window.addEventListener('employeeNoReady', handleEmployeeNoReady);
    return () => window.removeEventListener('employeeNoReady', handleEmployeeNoReady);
  }, [bcService]);

  // Close notification panel on outside click
  useEffect(() => {
    if (!notifPanelOpen) return;
    const handleClick = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) {
        setNotifPanelOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [notifPanelOpen]);

  const dismissAuthNotification = () => {
    setAuthIncomplete(false);
    localStorage.removeItem('adminAuthIncomplete');
    localStorage.removeItem('adminAuthEnvironment');
    localStorage.removeItem('adminAuthErrorCode');
  };

  const toggleMobileMenu = () => setMobileMenuOpen(!mobileMenuOpen);
  const toggleUserMenu = () => setUserMenuOpen(!userMenuOpen);

  const handleNotifClick = async (notif) => {
    setSelectedNotif(notif);
    setNotifPanelOpen(false);
    if (!notif.seen) {
      // Optimistically mark as seen in UI
      setNotifications(prev =>
        prev.map(n => n.systemId === notif.systemId ? { ...n, seen: true } : n)
      );
      // Persist to BC API (fire-and-forget)
      bcService.markNotificationSeen(notif.systemId);
    }
  };

  const unseenCount = notifications.filter(n => !n.seen).length;

  const navItems = [
    { name: 'Homepage', icon: Home, path: '/portal/dashboard' },
    { name: 'Documents', icon: FileText, path: '/portal/documents' }
  ];

  const formatNotifDate = (date, time) => {
    if (!date) return '';
    const parts = [date];
    if (time) parts.push(time.substring(0, 5));
    return parts.join(' • ');
  };

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      {/* Top navigation */}
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex">
              {/* Mobile menu button */}
              <div className="flex items-center md:hidden">
                <button
                  type="button"
                  className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500"
                  onClick={toggleMobileMenu}
                >
                  <span className="sr-only">Open main menu</span>
                  {mobileMenuOpen ? (
                    <X className="block h-6 w-6" />
                  ) : (
                    <Menu className="block h-6 w-6" />
                  )}
                </button>
              </div>

              {/* Portal logo */}
              <div className="flex-shrink-0 flex items-center">
                <a
                  href="https://www.siriusapp.co.uk/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-indigo-600 text-xl font-bold hover:text-indigo-700"
                >
                  Sirius Connect 365
                </a>
              </div>

              {/* Desktop navigation */}
              <div className="hidden md:ml-6 md:flex md:space-x-8">
                {navItems.map((item) => (
                  <Link
                    key={item.name}
                    to={item.path}
                    className="inline-flex items-center px-1 pt-1 border-b-2 border-transparent hover:border-indigo-500 text-sm font-medium text-gray-500 hover:text-gray-900"
                  >
                    <item.icon className="mr-2 h-5 w-5" />
                    {item.name}
                  </Link>
                ))}
              </div>
            </div>

            <div className="flex items-center">
              {/* Environment indicator */}
              {activeEnvironment && (
                <div className="hidden md:flex items-center mr-4">
                  <span className={`px-2 py-1 text-xs font-medium rounded-full max-w-[200px] overflow-hidden ${
                    (activeEnvironment.type || '').toLowerCase() === 'production'
                      ? 'bg-green-100 text-green-800'
                      : (activeEnvironment.type || '').toLowerCase() === 'sandbox'
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-gray-100 text-gray-800'
                  }`}>
                    <span className="truncate inline-block max-w-full" title={activeEnvironment.name || 'Unknown'}>
                      {activeEnvironment.name || 'Unknown'}
                    </span>
                  </span>
                </div>
              )}

              {/* Notifications bell */}
              <div className="relative mr-3" ref={notifRef}>
                <button
                  onClick={() => { setNotifPanelOpen(!notifPanelOpen); setUserMenuOpen(false); }}
                  className="relative p-1 rounded-full text-gray-400 hover:text-gray-500 focus:outline-none transition-colors"
                  aria-label="View notifications"
                >
                  <Bell className="h-6 w-6" />
                  {unseenCount > 0 && (
                    <span className="absolute -top-1 -right-1 h-4 w-4 bg-red-500 rounded-full flex items-center justify-center shadow-sm">
                      <span className="text-white font-bold leading-none" style={{ fontSize: '9px' }}>
                        {unseenCount > 9 ? '9+' : unseenCount}
                      </span>
                    </span>
                  )}
                </button>

                {notifPanelOpen && (
                  <div className="origin-top-right absolute right-0 mt-2 w-80 rounded-xl shadow-2xl bg-white ring-1 ring-black ring-opacity-5 z-30 overflow-hidden">
                    <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between bg-gray-50">
                      <div className="flex items-center gap-2">
                        <Bell className="h-4 w-4 text-indigo-500" />
                        <span className="text-sm font-semibold text-gray-900">Notifications</span>
                      </div>
                      {unseenCount > 0 && (
                        <span className="px-2 py-0.5 text-xs font-semibold bg-indigo-100 text-indigo-700 rounded-full">
                          {unseenCount} new
                        </span>
                      )}
                    </div>

                    {notifications.length === 0 ? (
                      <div className="px-4 py-8 text-center">
                        <Bell className="h-8 w-8 text-gray-200 mx-auto mb-2" />
                        <p className="text-sm text-gray-400">No notifications</p>
                      </div>
                    ) : (
                      <ul className="divide-y divide-gray-50 max-h-72 overflow-y-auto">
                        {notifications.map(notif => (
                          <li
                            key={notif.systemId}
                            onClick={() => handleNotifClick(notif)}
                            className={`px-4 py-3 flex items-start gap-3 cursor-pointer hover:bg-gray-50 transition-colors ${notif.seen ? '' : 'bg-indigo-50/50'}`}
                          >
                            <span className={`mt-2 h-2 w-2 flex-shrink-0 rounded-full ${!notif.seen ? 'bg-indigo-500' : 'bg-gray-300'}`} />
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm leading-snug ${!notif.seen ? 'font-medium text-gray-900' : 'text-gray-400'}`}>
                                {notif.message}
                              </p>
                              <p className="text-xs text-gray-400 mt-0.5">
                                {formatNotifDate(notif.createdDate, notif.createdTime)}
                              </p>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>

              {/* User menu */}
              <div className="relative">
                <button
                  className="flex items-center gap-2 text-sm rounded-full focus:outline-none"
                  onClick={() => { toggleUserMenu(); setNotifPanelOpen(false); }}
                >
                  <div className="h-8 w-8 rounded-full bg-indigo-600 flex items-center justify-center">
                    <span className="text-sm font-semibold text-white">{user?.name?.charAt(0) || 'U'}</span>
                  </div>
                  <span className="hidden md:flex md:items-center gap-1">
                    <span className="text-sm text-gray-700">{user?.name || 'User'}</span>
                    <ChevronDown className="h-4 w-4 text-gray-400" />
                  </span>
                </button>

                {userMenuOpen && (
                  <div className="origin-top-right absolute right-0 mt-2 w-64 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-20">
                    <div className="py-1" role="menu">
                      <div className="block px-4 py-2 text-sm text-gray-900 border-b border-gray-200">
                        <p className="font-medium truncate">{user?.name}</p>
                        <p className="text-xs text-gray-500 mt-1 truncate" title={user?.email}>{user?.email}</p>
                      </div>
                      <Link
                        to="/portal/profile"
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        <div className="flex items-center">
                          <User className="mr-2 h-4 w-4 text-gray-500" />
                          Your Profile
                        </div>
                      </Link>

                      {isAdmin && (
                        <button
                          onClick={() => navigate('/admin/dashboard')}
                          className="w-full text-left block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          <div className="flex items-center">
                            <User className="mr-2 h-4 w-4 text-indigo-500" />
                            Switch to Admin Portal
                          </div>
                        </button>
                      )}

                      <button
                        onClick={logout}
                        className="w-full text-left block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 border-t border-gray-200"
                      >
                        <div className="flex items-center">
                          <LogOut className="mr-2 h-4 w-4 text-gray-500" />
                          Sign out
                        </div>
                      </button>
                    </div>
                  </div>
                )}
              </div>

            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden">
            <div className="pt-2 pb-3 space-y-1">
              {navItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.path}
                  className="flex items-center px-3 py-2 text-base font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <item.icon className="mr-3 h-5 w-5 text-gray-400" />
                  {item.name}
                </Link>
              ))}
              {isAdmin && (
                <div className="px-3 py-2">
                  <PortalSwitcher currentPortal="user" className="w-full" />
                </div>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Admin notification for auth incomplete */}
      {isAdmin && authIncomplete && (
        <div className="max-w-7xl mx-auto sm:px-6 lg:px-8 w-full">
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-0 mt-6 shadow-sm">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-sm font-medium text-yellow-800">
                  Business Central Environment Setup Incomplete
                </h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>
                    The environment "{authEnvironment}" requires additional authorization setup in Business Central.
                    Missing refresh token for API access. Please go to Admin Portal to complete the setup.
                  </p>
                </div>
                <div className="mt-4">
                  <div className="-mx-2 -my-1.5 flex">
                    <button
                      onClick={() => navigate('/admin/authorization')}
                      className="bg-yellow-50 px-2 py-1.5 rounded-md text-sm font-medium text-yellow-800 hover:bg-yellow-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-yellow-50 focus:ring-yellow-600 mr-2"
                    >
                      Go to Admin Portal
                    </button>
                    <button
                      onClick={dismissAuthNotification}
                      className="bg-yellow-50 px-2 py-1.5 rounded-md text-sm font-medium text-yellow-800 hover:bg-yellow-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-yellow-50 focus:ring-yellow-600"
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main content */}
      <main className="flex-grow max-w-7xl mx-auto py-6 sm:px-6 lg:px-8 w-full">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-auto">
        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
          <p className="text-sm text-gray-500 text-center">
            &copy; {new Date().getFullYear()} SiriusConnect365. All rights reserved.
          </p>
        </div>
      </footer>

      {/* Notification detail overlay */}
      {selectedNotif && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setSelectedNotif(null)}
          />
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 animate-in">
            {/* Header */}
            <div className="flex items-center gap-3 mb-4">
              <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                <Bell className="h-5 w-5 text-indigo-600" />
              </div>
              <div>
                <h4 className="text-sm font-semibold text-gray-900">Notification</h4>
                <p className="text-xs text-gray-400">
                  {formatNotifDate(selectedNotif.createdDate, selectedNotif.createdTime)}
                </p>
              </div>
            </div>

            {/* Message */}
            <p className="text-sm text-gray-700 leading-relaxed bg-gray-50 rounded-lg px-4 py-3">
              {selectedNotif.message}
            </p>

            {/* Close button */}
            <button
              onClick={() => setSelectedNotif(null)}
              className="mt-5 w-full py-2.5 px-4 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserPortalLayout;
